import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, clearToken, DPP_BASE_URL } from '../dppApi';
import '../App.css';

// A user's collection: every product they currently or previously owned.
const UserCollection = () => {
    const navigate = useNavigate();
    const token = getToken('user');
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docPanel, setDocPanel] = useState(null);

    useEffect(() => {
        if (!token) { navigate('/user'); return; }
        apiFetch('/api/user/collection', { token })
            .then(setItems)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    const logout = () => { clearToken('user'); navigate('/user'); };

    const DOC_META = {
        insurance: { label: 'Upload insurance', fileLabel: 'Insurance document', issuerLabel: 'Insurer', placeholder: "e.g. Lloyd's of London" },
        appraisal: { label: 'Upload appraisal', fileLabel: 'Appraisal document', issuerLabel: 'Appraiser', placeholder: 'e.g. GIA' },
    };

    const openDoc = (productId, docType) =>
        setDocPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeDoc = () => setDocPanel(null);

    const uploadDoc = async () => {
        if (!docPanel.file) return;
        setDocPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', docPanel.file);
            form.append('doc_type', docPanel.docType);
            if (docPanel.issuedBy) form.append('issued_by', docPanel.issuedBy);
            if (docPanel.issuedDate) form.append('issued_date', docPanel.issuedDate);
            await apiFetch(`/api/products/${docPanel.productId}/documents/owner`, { method: 'POST', token, body: form, isForm: true });
            setDocPanel(p => ({ ...p, busy: false, done: true }));
        } catch (err) {
            setDocPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    return (
        <div className="dpp-page">
            <div className="dpp-header">
                <h1>My Collection</h1>
                <button className="dpp-link-button" onClick={logout}>Log out</button>
            </div>

            {loading && <p className="dpp-muted">Loading…</p>}
            {error && <p className="dpp-error">{error}</p>}
            {!loading && !error && items.length === 0 && <p className="dpp-muted">You don't own any products yet.</p>}

            <div className="dpp-grid">
                {items.map((p) => (
                    <div className="dpp-card dpp-collection-item" key={p.product_id}>
                        {p.image_url && p.image_url.startsWith('http') && (
                            <img className="dpp-collection-image" src={p.image_url} alt={p.name} />
                        )}
                        <strong>{p.name}</strong>
                        {!p.is_current_owner && <span className="dpp-badge">Previously owned</span>}
                        <a href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">View passport</a>
                        {p.is_current_owner && Object.keys(DOC_META).map(docType => (
                            <button
                                key={docType}
                                className="dpp-link-button"
                                onClick={() =>
                                    docPanel?.productId === p.product_id && docPanel?.docType === docType
                                        ? closeDoc()
                                        : openDoc(p.product_id, docType)
                                }
                            >
                                {docPanel?.productId === p.product_id && docPanel?.docType === docType
                                    ? 'Cancel'
                                    : DOC_META[docType].label}
                            </button>
                        ))}
                        {docPanel?.productId === p.product_id && (
                            <div className="dpp-transfer-panel">
                                {docPanel.done ? (
                                    <p className="dpp-transfer-label" style={{ color: 'green' }}>
                                        {DOC_META[docPanel.docType]?.label.replace('Upload', '')} uploaded.
                                    </p>
                                ) : (
                                    <>
                                        <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.fileLabel}</label>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={e => setDocPanel(p => ({ ...p, file: e.target.files[0] || null }))}
                                        />
                                        <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.issuerLabel} (optional)</label>
                                        <input
                                            className="dpp-input"
                                            placeholder={DOC_META[docPanel.docType]?.placeholder}
                                            value={docPanel.issuedBy}
                                            onChange={e => setDocPanel(p => ({ ...p, issuedBy: e.target.value }))}
                                        />
                                        <label className="dpp-transfer-label">Date (optional)</label>
                                        <input
                                            className="dpp-input"
                                            type="date"
                                            value={docPanel.issuedDate}
                                            onChange={e => setDocPanel(p => ({ ...p, issuedDate: e.target.value }))}
                                        />
                                        {docPanel.error && <p className="dpp-error">{docPanel.error}</p>}
                                        <button
                                            className="dpp-button"
                                            onClick={uploadDoc}
                                            disabled={docPanel.busy || !docPanel.file}
                                        >
                                            {docPanel.busy ? 'Uploading…' : 'Upload'}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserCollection;
